'use client';

import { useMemo } from 'react';
import { GavValues, Purchase } from './types';
import { ToolsTranslations } from '@/lib/translations/types';

interface Props {
  values: GavValues;
  onValuesChange: (values: GavValues) => void;
  translations: ToolsTranslations['gav'];
}

export default function CalculatorInputs({ values, onValuesChange, translations }: Props) {
  const handleNumPurchasesChange = (num: number) => {
    const n = Math.max(1, Math.min(6, Math.floor(num)));
    let purchases: Purchase[] = values.purchases.slice();
    if (purchases.length < n) {
      purchases = [...purchases, ...Array.from({ length: n - purchases.length }, () => ({ shares: 0, pricePerShare: 0, fee: 0 }))];
    } else if (purchases.length > n) {
      purchases = purchases.slice(0, n);
    }
    onValuesChange({ ...values, numPurchases: n, purchases });
  };

  const handleFeeModeChange = (mode: 'without' | 'with') => onValuesChange({ ...values, feeMode: mode });

  const updatePurchase = (index: number, field: keyof Purchase, raw: string) => {
    const v = Number(raw.replace(/\s/g, '').replace(/,/g, '.'));
    const purchases = values.purchases.slice();
    purchases[index] = { ...purchases[index], [field]: isNaN(v) ? 0 : v } as Purchase;
    onValuesChange({ ...values, purchases });
  };

  const purchasePanels = useMemo(() => {
    return values.purchases.map((p, i) => (
      <div key={i} className="border rounded-lg shadow-sm">
        <div className="bg-blue-50 text-blue-900 font-semibold px-4 py-2 rounded-t-lg border-b">{translations.purchaseTitle} {i + 1}</div>
        <div className="space-y-3 p-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{translations.shares}</label>
            <div className="relative">
              <input type="text" value={(p.shares || 0).toLocaleString('sv-SE')} onChange={(e) => updatePurchase(i, 'shares', e.target.value)} className="w-full rounded-md border-gray-300 shadow-sm pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="0" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">{translations.times}</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{translations.pricePerShare}</label>
            <div className="relative">
              <input type="text" value={(p.pricePerShare || 0).toLocaleString('sv-SE')} onChange={(e) => updatePurchase(i, 'pricePerShare', e.target.value)} className="w-full rounded-md border-gray-300 shadow-sm pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="0" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">kr</span>
            </div>
          </div>
          {values.feeMode === 'with' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{translations.fee}</label>
              <div className="relative">
                <input type="text" value={(p.fee || 0).toLocaleString('sv-SE')} onChange={(e) => updatePurchase(i, 'fee', e.target.value)} className="w-full rounded-md border-gray-300 shadow-sm pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="0" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">kr</span>
              </div>
            </div>
          )}
        </div>
      </div>
    ));
  }, [values.purchases, values.feeMode, translations]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{translations.chooseNumPurchases}</label>
          <select value={values.numPurchases} onChange={(e) => handleNumPurchasesChange(Number(e.target.value))} className="w-full rounded-md border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
            {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n} {translations.times}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{translations.feeMode}</label>
          <select value={values.feeMode} onChange={(e) => handleFeeModeChange(e.target.value as 'without' | 'with')} className="w-full rounded-md border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
            <option value="without">{translations.feeModeWithout}</option>
            <option value="with">{translations.feeModeWith}</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {purchasePanels}
      </div>
    </div>
  );
}


